import { useState, useCallback, useRef, useEffect } from 'react';
import axios from '@/utils/axios';
import { getSavedLanguage } from '@/utils/translations';
import { toast } from 'sonner';

// Cache translations to avoid redundant API calls
const translationCache = new Map();
let hasShownBudgetWarning = false;

export function useContentTranslation() {
  const [translating, setTranslating] = useState(false);
  const [translations, setTranslations] = useState({});
  const [translationFailed, setTranslationFailed] = useState(false);
  const pendingRef = useRef(new Set());

  const translateTexts = useCallback(async (texts) => {
    const currentLang = getSavedLanguage();
    
    // No translation needed for English (content is stored in English)
    if (currentLang === 'en') {
      const result = {};
      texts.forEach(text => { result[text] = text; });
      return result;
    }

    // Check cache first
    const cacheKey = `${currentLang}`;
    const cached = translationCache.get(cacheKey) || {};
    
    // Filter out already cached texts
    const textsToTranslate = texts.filter(text => !cached[text] && !pendingRef.current.has(text));
    
    // If all texts are cached, return from cache
    if (textsToTranslate.length === 0) {
      const result = {};
      texts.forEach(text => { result[text] = cached[text] || text; });
      return result;
    }

    // Mark as pending
    textsToTranslate.forEach(text => pendingRef.current.add(text));
    
    setTranslating(true);
    try {
      const response = await axios.post('/translate/content', {
        texts: textsToTranslate,
        target_language: currentLang
      });
      
      // Merge new translations with cache
      const newTranslations = response.data.translations || {};
      
      // Check if translations are actually different (API returned translated content)
      const hasActualTranslations = Object.keys(newTranslations).some(key => newTranslations[key] !== key);
      
      if (hasActualTranslations) {
        const updatedCache = { ...cached, ...newTranslations };
        translationCache.set(cacheKey, updatedCache);
        
        // Build result combining cache and new translations
        const result = {};
        texts.forEach(text => {
          result[text] = updatedCache[text] || text;
        });
        
        setTranslations(prev => ({ ...prev, ...result }));
        setTranslationFailed(false);
        
        // Clear pending
        textsToTranslate.forEach(text => pendingRef.current.delete(text));
        
        return result;
      } else {
        // API returned same text - translation might have failed silently
        if (!hasShownBudgetWarning) {
          hasShownBudgetWarning = true;
          toast.warning('Translation service temporarily unavailable. Content shown in original language.', {
            duration: 5000
          });
        }
        setTranslationFailed(true);
        const result = {};
        texts.forEach(text => { result[text] = text; });
        textsToTranslate.forEach(text => pendingRef.current.delete(text));
        return result;
      }
    } catch (error) {
      console.error('Translation failed:', error);
      if (!hasShownBudgetWarning) {
        hasShownBudgetWarning = true;
        toast.warning('Translation service temporarily unavailable. Content shown in original language.', {
          duration: 5000
        });
      }
      setTranslationFailed(true);
      // Return original texts on error
      const result = {};
      texts.forEach(text => { result[text] = text; });
      textsToTranslate.forEach(text => pendingRef.current.delete(text));
      return result;
    } finally {
      setTranslating(false);
    }
  }, []);

  const getTranslation = useCallback((text) => {
    const currentLang = getSavedLanguage();
    if (currentLang === 'en') return text;
    
    const cached = translationCache.get(currentLang) || {};
    return translations[text] || cached[text] || text;
  }, [translations]);

  const clearCache = useCallback(() => {
    translationCache.clear();
    setTranslations({});
    hasShownBudgetWarning = false;
  }, []);

  return {
    translateTexts,
    getTranslation,
    translating,
    translations,
    translationFailed,
    clearCache
  };
}

// Simple component wrapper for translated text
export function TranslatedText({ text, className = '' }) {
  const [translated, setTranslated] = useState(text);
  const [loading, setLoading] = useState(false);
  const hasTranslated = useRef(false);

  const currentLang = getSavedLanguage();
  
  // Only translate on mount if not English
  if (currentLang !== 'en' && !hasTranslated.current && text) {
    hasTranslated.current = true;
    const cacheKey = currentLang;
    const cached = translationCache.get(cacheKey);
    
    if (cached && cached[text]) {
      setTranslated(cached[text]);
    } else {
      setLoading(true);
      axios.post('/translate/content', {
        texts: [text],
        target_language: currentLang
      }).then(response => {
        const newTranslations = response.data.translations || {};
        const updatedCache = { ...(translationCache.get(cacheKey) || {}), ...newTranslations };
        translationCache.set(cacheKey, updatedCache);
        setTranslated(newTranslations[text] || text);
      }).catch(() => {
        setTranslated(text);
      }).finally(() => {
        setLoading(false);
      });
    }
  }

  if (loading) {
    return <span className={`${className} animate-pulse bg-slate-200 rounded`}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>;
  }

  return <span className={className}>{translated}</span>;
}

export default useContentTranslation;
